import React, { useEffect, useState } from "react";
import useGetRequest from "../../hooks/useGetRequest";
import { ISettings } from "../../types/settings.type";
import { api } from "../../api/api";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Spin,
  Switch,
} from "antd";
import dayjs from "dayjs";
import { useAuth } from "../../contexts/auth.context";
import { hasAdminAccess } from "../../utils/roleUtils";

export default function GlobalSettings() {
  const { data, loading, error, refresh } = useGetRequest("/settings");
  const { data: maintenanceData, refresh: refreshMaintenance } = useGetRequest("/settings/maintenance-mode");
  const { authState } = useAuth();
  const [settings, setSettings] = useState<ISettings | null>(null);
  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMaintenanceLoading, setIsMaintenanceLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isAdmin = hasAdminAccess(authState.user?.role);

  useEffect(() => {
    if (data?.settings) {
      setSettings(data.settings);
    }
  }, [data]);

  useEffect(() => {
    if (maintenanceData?.maintenanceMode !== undefined) {
      setMaintenanceMode(maintenanceData.maintenanceMode);
    }
  }, [maintenanceData]);

  const updateSettings = async (values: ISettings) => {
    setIsLoading(true);

    try {
      const response = await api.post("/settings", values);

      message.success("Данные успешно обновлены");
      refresh();
      setIsModalVisible(false);
    } catch (error) {
      message.error("Ошибка при обновлении данных.");
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = () => {
    setIsEditing(!!settings);
    setIsModalVisible(true);
  };

  const toggleMaintenanceMode = async (checked: boolean) => {
    setIsMaintenanceLoading(true);
    try {
      await api.post("/settings/maintenance-mode", {
        maintenanceMode: checked,
      });
      setMaintenanceMode(checked);
      message.success(
        checked
          ? "Режим обслуживания включен. Все пользователи, кроме админов, будут заблокированы."
          : "Режим обслуживания выключен. Доступ восстановлен для всех пользователей."
      );
      refreshMaintenance();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при изменении режима обслуживания");
    } finally {
      setIsMaintenanceLoading(false);
    }
  };

  if (error) {
    return <Alert message="Ошибка при загрузке данных" type="error" showIcon />;
  }

  if (loading) {
    return <Spin tip="Загрузка..." />;
  }

  return (
    <div>
      {isAdmin && (
        <Card 
          title="Режим обслуживания" 
          style={{ width: 500, marginBottom: 16 }}
          extra={
            <Switch
              checked={maintenanceMode}
              onChange={toggleMaintenanceMode}
              loading={isMaintenanceLoading}
              checkedChildren="Включен"
              unCheckedChildren="Выключен"
            />
          }
        >
          <p style={{ margin: 0, color: maintenanceMode ? "#ff4d4f" : "#52c41a" }}>
            {maintenanceMode
              ? "⚠️ Режим обслуживания активен. Все пользователи, кроме админов, заблокированы."
              : "✅ Система работает в обычном режиме. Все пользователи имеют доступ."}
          </p>
          <p style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}>
            При включении режима обслуживания все запросы от неадминистраторов будут блокированы с сообщением о технических работах.
          </p>
        </Card>
      )}
      {settings ? (
        <Card title="Общие настройки" style={{ width: 500 }}>
          <p>
            <strong>Юк цена USD:</strong> {settings.yukPriceUSD}
          </p>
          <p>
            <strong>Юк цена TJS:</strong> {settings.yukPriceTJS}
          </p>
          <p>
            <strong>Курс нижний лимит:</strong> {settings.exchangeRateLowLimit}
          </p>
          <p>
            <strong>Курс верхний лимит:</strong> {settings.exchangeRateUpLimit}
          </p>
          <p>
            <strong>Минут лимит:</strong> {settings.minuteLimit}
          </p>
          <p>
            <strong>Нижний лимит кг производства %:</strong>{" "}
            {settings.kgReportLowLimit}
          </p>
          <p>
            <strong>Верхний лимит кг производства %:</strong>{" "}
            {settings.kgReportUpLimit}
          </p>
          <p>
            <strong>Нижний лимит кг заказ %:</strong> {settings.kgOrderLowLimit}
          </p>
          <p>
            <strong>Верхний лимит кг заказ %:</strong> {settings.kgOrderUpLimit}
          </p>
          <p>
            <strong>Дата лимит:</strong> {settings.dateLimit ? new Date(settings.dateLimit).toLocaleDateString() : 'Не указана'}
          </p>
          <p>
            <strong>Макс имя филлера:</strong> {settings.maxFillerName || 'Не указано'}
          </p>
          <p>
            <strong>Лимит продаж цены:</strong> {settings.salesPriceLimit ? 'Включен' : 'Отключен'}
          </p>
          <p>
            <strong>Процент скидки за оплату сразу:</strong> {settings.paidImmediatelyDiscountPercent ?? 5}%
          </p>
          <Button type="primary" onClick={openModal} style={{ marginTop: 16 }}>
            Изменить
          </Button>
        </Card>
      ) : (
        <Card title="Настройки не найдены" style={{ width: 500 }}>
          <Button type="primary" onClick={openModal}>
            Добавить
          </Button>
        </Card>
      )}

      <Modal
        title={isEditing ? "Редактировать" : "Добавить"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Spin spinning={isLoading}>
          <Form
            initialValues={{
              ...settings,
              dateLimit: settings?.dateLimit ? dayjs(settings.dateLimit) : null
            }}
            onFinish={(values) => {
              const formattedValues = {
                ...values,
                dateLimit: values.dateLimit ? values.dateLimit.toDate() : null
              };
              updateSettings(formattedValues);
            }}
            layout="vertical"
            style={{ maxWidth: 500 }}
          >
            <Form.Item
              label="Цена Юк USD"
              name="yukPriceUSD"
              rules={[
                { required: true, message: "Пожалуйста, введите цена юк!" },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Цена Юк TJS"
              name="yukPriceTJS"
              rules={[
                { required: true, message: "Пожалуйста, введите цена юк!" },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Курс нижний лимит"
              name="exchangeRateLowLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите курс нижний лимит!",
                },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Юк лимит"
              name="yukLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите юк лимит!",
                },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Курс верхний лимит"
              name="exchangeRateUpLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите курс верхний лимит!",
                },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Лимит минут"
              name="minuteLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите минут лимит !",
                },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Нижний лимит кг производства %"
              name="kgReportLowLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите кг лимит !",
                },
              ]}
            >
              <InputNumber max={100} />
            </Form.Item>

            <Form.Item
              label="Верхний лимит кг производства %"
              name="kgReportUpLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите кг лимит !",
                },
              ]}
            >
              <InputNumber max={100} />
            </Form.Item>

            <Form.Item
              label="Нижний лимит кг заказа %"
              name="kgOrderLowLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите кг лимит !",
                },
              ]}
            >
              <InputNumber max={100} />
            </Form.Item>

            <Form.Item
              label="Верхний лимит кг заказа %"
              name="kgOrderUpLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите кг лимит !",
                },
              ]}
            >
              <InputNumber max={100} />
            </Form.Item>

            <Form.Item
              label="Дата лимит"
              name="dateLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, выберите дату лимит!",
                },
              ]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="Макс имя филлера"
              name="maxFillerName"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите макс имя филлера!",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Лимит продаж цены"
              name="salesPriceLimit"
              valuePropName="checked"
              tooltip="Если включено, нельзя создать заказ с ценой меньше минимальной (кроме админа)"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              label="Процент скидки за оплату сразу"
              name="paidImmediatelyDiscountPercent"
              rules={[
                {
                  required: false,
                  message: "Пожалуйста, введите процент скидки!",
                },
                {
                  type: "number",
                  min: 0,
                  max: 100,
                  message: "Процент должен быть от 0 до 100",
                },
              ]}
              tooltip="Процент скидки от минимальной цены при оплате сразу (по умолчанию 5%)"
            >
              <InputNumber min={0} max={100} />
            </Form.Item>

            <Button type="primary" htmlType="submit" loading={isLoading}>
              {isEditing ? "Обновить" : "Добавить"}
            </Button>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
}
